import Modal from "@/components/base/Modal";
import Badge from "@/components/base/Badge";
import { useContrats } from "@/pages/proprietaire/contrats/ContratsProvider";
import { contratReference } from "@/pages/proprietaire/contrats/types";
import { monthLabel } from "@/pages/proprietaire/loyers/periods";
import { usePaiements } from "@/pages/proprietaire/paiements/PaiementsProvider";
import { MODE_PAIEMENT_ICONS, MODE_PAIEMENT_LABELS } from "@/pages/proprietaire/paiements/types";
import { formatDate, formatFcfa } from "@/utils/format";
import type { Loyer } from "@/pages/proprietaire/loyers/types";

interface HistoriquePaiementsModalProps {
  loyer: Loyer | null;
  onClose: () => void;
}

export default function HistoriquePaiementsModal({ loyer, onClose }: HistoriquePaiementsModalProps) {
  const { getContrat } = useContrats();
  const { paiementsByContrat } = usePaiements();

  if (!loyer) return null;

  const contrat = getContrat(loyer.contratId);
  const paiements = paiementsByContrat(loyer.contratId);
  const paiementsPeriode = paiements.filter((item) => item.periode === loyer.periode);

  return (
    <Modal
      open={Boolean(loyer)}
      onClose={onClose}
      size="md"
      title="Historique des paiements"
      description={`Contrat ${contrat ? contratReference(contrat.id) : ""} — loyer de ${monthLabel(loyer.periode)}.`}
    >
      <div className="flex flex-col gap-4">
        <div className="grid grid-cols-3 gap-3 rounded-lg border border-background-200 bg-background-100 p-4">
          <div className="flex flex-col">
            <span className="text-[11px] text-foreground-500">Montant</span>
            <span className="font-heading text-sm font-semibold text-foreground-950">{formatFcfa(loyer.montant)}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[11px] text-foreground-500">Réglé</span>
            <span className="font-heading text-sm font-semibold text-secondary-700">{formatFcfa(loyer.totalPaye)}</span>
          </div>
          <div className="flex flex-col">
            <span className="text-[11px] text-foreground-500">Reste dû</span>
            <span className="font-heading text-sm font-semibold text-primary-700">{formatFcfa(loyer.reste)}</span>
          </div>
        </div>

        <div>
          <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
            Paiements sur cette période
          </p>
          {paiementsPeriode.length === 0 ? (
            <p className="mt-2 rounded-md border border-dashed border-background-300 px-4 py-3 text-sm text-foreground-500">
              Aucun paiement enregistré pour cette période.
            </p>
          ) : (
            <ul className="mt-2 flex flex-col gap-2">
              {paiementsPeriode.map((paiement) => (
                <li
                  key={paiement.id}
                  className="flex items-center gap-3 rounded-md border border-background-200 px-3.5 py-2.5"
                >
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
                    <i className={`${MODE_PAIEMENT_ICONS[paiement.mode]} text-base`} aria-hidden="true" />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-label font-medium text-foreground-950">
                      {formatFcfa(paiement.montant)} · {MODE_PAIEMENT_LABELS[paiement.mode]}
                    </span>
                    <span className="block truncate text-xs text-foreground-500">
                      {formatDate(paiement.date)}
                      {paiement.reference ? ` · ${paiement.reference}` : ""}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div>
          <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">
            Tout l'historique du contrat
          </p>
          {paiements.length === 0 ? (
            <p className="mt-2 rounded-md border border-dashed border-background-300 px-4 py-3 text-sm text-foreground-500">
              Aucun paiement enregistré sur ce contrat.
            </p>
          ) : (
            <ul className="mt-2 flex flex-col gap-2">
              {paiements.map((paiement) => (
                <li
                  key={paiement.id}
                  className="flex items-center justify-between gap-3 rounded-md border border-background-200 px-3.5 py-2.5"
                >
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate text-sm font-label font-medium text-foreground-950">
                      {monthLabel(paiement.periode)}
                    </span>
                    <span className="truncate text-xs text-foreground-500">
                      {MODE_PAIEMENT_LABELS[paiement.mode]} · {formatDate(paiement.date)}
                    </span>
                  </span>
                  <span className="shrink-0 text-sm font-heading font-semibold text-foreground-950">
                    {formatFcfa(paiement.montant)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-col-reverse gap-2 border-t border-background-200 pt-4 sm:flex-row sm:justify-between sm:items-center">
          <Badge tone="neutral" icon="ri-information-line">
            Un règlement total fera passer le loyer à « Payé ».
          </Badge>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center whitespace-nowrap rounded-md border border-foreground-200 bg-background-50 px-5 py-2.5 text-sm font-label font-medium text-foreground-900 transition-colors hover:bg-background-100 cursor-pointer"
          >
            Fermer
          </button>
        </div>
      </div>
    </Modal>
  );
}